import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {createContext, type ReactNode, useContext, useMemo} from "react";

import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {Country} from "@vesta/vos-types/modules/platformLevel/geography/domain/Country";
import type {IndiaCity} from "@vesta/vos-types/modules/platformLevel/geography/domain/IndiaCity";
import type {IndiaPincode} from "@vesta/vos-types/modules/platformLevel/geography/domain/IndiaPincode";
import type {IndiaState} from "@vesta/vos-types/modules/platformLevel/geography/domain/IndiaState";
import type {IndiaZone} from "@vesta/vos-types/modules/platformLevel/geography/domain/IndiaZone";


export type GeographyCollections = {
    countries: ReadonlyArray<Country>;
    indiaCities: ReadonlyArray<IndiaCity>;
    indiaPincodes: ReadonlyArray<IndiaPincode>;
    indiaStates: ReadonlyArray<IndiaState>;
    indiaZones: ReadonlyArray<IndiaZone>;
};

export type GeographyContextData = GeographyCollections & {
    countriesById: ReadonlyMap<Uuid, Country>;
    indiaCitiesById: ReadonlyMap<Uuid, IndiaCity>;
    indiaPincodesById: ReadonlyMap<Uuid, IndiaPincode>;
    indiaStatesById: ReadonlyMap<Uuid, IndiaState>;
    indiaZonesById: ReadonlyMap<Uuid, IndiaZone>;
    resolveAdditionalColumnValue: (additionalColumnType: string, value: unknown) => string | null;
};

const GeographyContext = createContext<GeographyContextData | undefined>(undefined);

function indexById<TItem extends {id: Uuid}>(items: ReadonlyArray<TItem>): ReadonlyMap<Uuid, TItem> {
    return new Map(items.map((item) => [item.id, item]));
}

export function GeographyProvider({children, geography}: {children: ReactNode; geography: GeographyCollections}) {
    const value = useMemo<GeographyContextData>(() => {
        const countriesById = indexById(geography.countries);
        const indiaCitiesById = indexById(geography.indiaCities);
        const indiaPincodesById = indexById(geography.indiaPincodes);
        const indiaStatesById = indexById(geography.indiaStates);
        const indiaZonesById = indexById(geography.indiaZones);
        const coordinateAdditionalColumnTypes = new Set<string>([
            UdoColumnType.geographyLatitude,
            UdoColumnType.geographyLongitude,
        ]);
        const referenceResolvers = new Map<string, (id: Uuid) => string>([
            [UdoColumnType.geographyCountries, (id) => countriesById.get(id)?.name ?? "-"],
            [UdoColumnType.geographyIndiaCities, (id) => indiaCitiesById.get(id)?.name ?? "-"],
            [UdoColumnType.geographyIndiaPincodes, (id) => indiaPincodesById.get(id)?.pincode ?? "-"],
            [UdoColumnType.geographyIndiaStates, (id) => indiaStatesById.get(id)?.name ?? "-"],
            [UdoColumnType.geographyIndiaZones, (id) => indiaZonesById.get(id)?.name ?? "-"],
        ]);

        return {
            ...geography,
            countriesById: countriesById,
            indiaCitiesById: indiaCitiesById,
            indiaPincodesById: indiaPincodesById,
            indiaStatesById: indiaStatesById,
            indiaZonesById: indiaZonesById,
            resolveAdditionalColumnValue: (additionalColumnType, rawValue) => {
                if (coordinateAdditionalColumnTypes.has(additionalColumnType)) {
                    return typeof rawValue === "number" || typeof rawValue === "string" ? String(rawValue) : "-";
                }

                if (typeof rawValue !== "string") {
                    return null;
                }
                const resolver = referenceResolvers.get(additionalColumnType);
                return resolver?.(rawValue as Uuid) ?? null;
            },
        };
    }, [geography]);

    return <GeographyContext.Provider value={value}>{children}</GeographyContext.Provider>;
}

export function useGeography(): GeographyContextData {
    const geography = useContext(GeographyContext);
    if (geography === undefined) {
        throw new Error("GeographyContext.Provider not found");
    }
    return geography;
}

export function useOptionalGeography(): GeographyContextData | null {
    return useContext(GeographyContext) ?? null;
}
